import React, { useState } from 'react';
import {
  Drawer, Box, Typography, IconButton, Divider, Chip,
  CircularProgress, LinearProgress, Tooltip, Collapse,
} from '@mui/material';
import {
  Close as CloseIcon,
  CheckCircle as PassIcon,
  Cancel as FailIcon,
  Help as UnknownIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  Shield as ShieldIcon,
  Security as SecurityIcon,
} from '@mui/icons-material';
import api from '../../utils/api';

interface SpamAnalysisDrawerProps {
  open: boolean;
  onClose: () => void;
  email: { id: string; messageId?: string; from?: string; subject?: string };
}

interface Layer {
  id: string;
  name: string;
  description: string;
  status: 'pass' | 'fail' | 'neutral' | 'not_checked' | 'unknown';
  score: number;
  triggered: Array<{ name: string; score: number; description: string }>;
}

interface AnalysisResult {
  found: boolean;
  score?: number;
  action?: string;
  scanTime?: string;
  scannedAt?: string;
  from?: string;
  layers: Layer[];
  allSymbols?: Array<{ name: string; score: number; description: string }>;
  summary: string;
}

const STATUS_CONFIG = {
  pass:        { color: '#16a34a', bg: '#dcfce7', icon: <PassIcon sx={{ fontSize: 16, color: '#16a34a' }} />, label: 'Pass' },
  fail:        { color: '#dc2626', bg: '#fee2e2', icon: <FailIcon sx={{ fontSize: 16, color: '#dc2626' }} />, label: 'Fail' },
  neutral:     { color: '#d97706', bg: '#fef3c7', icon: <UnknownIcon sx={{ fontSize: 16, color: '#d97706' }} />, label: 'Neutral' },
  not_checked: { color: '#64748b', bg: '#f1f5f9', icon: <UnknownIcon sx={{ fontSize: 16, color: '#64748b' }} />, label: 'N/A' },
  unknown:     { color: '#64748b', bg: '#f1f5f9', icon: <UnknownIcon sx={{ fontSize: 16, color: '#64748b' }} />, label: 'Unknown' },
};

const getScoreColor = (score: number) => {
  if (score >= 15) return '#dc2626';
  if (score >= 8) return '#f59e0b';
  if (score >= 3) return '#d97706';
  return '#16a34a';
};

const getActionColor = (action: string) => {
  if (!action) return '#64748b';
  const a = action.toLowerCase();
  if (a.includes('reject')) return '#dc2626';
  if (a.includes('spam') || a.includes('quarantine')) return '#f59e0b';
  if (a.includes('no action') || a.includes('pass')) return '#16a34a';
  return '#d97706';
};

const SpamAnalysisDrawer: React.FC<SpamAnalysisDrawerProps> = ({ open, onClose, email }) => {
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState<AnalysisResult | null>(null);
  const [expandedLayers, setExpandedLayers] = useState<Set<string>>(new Set());
  const [showAllSymbols, setShowAllSymbols] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const analyse = async () => {
    setLoading(true);
    setError(null);
    try {
      const params = new URLSearchParams();
      if (email.messageId) params.set('messageId', email.messageId);
      else if (email.from) params.set('from', email.from);
      if (email.subject) params.set('subject', email.subject);
      const res = await api.get(`/spam/email-analysis?${params.toString()}`);
      setResult(res.data?.data || res.data);
    } catch (e: any) {
      setError(e?.response?.data?.message || 'Failed to fetch spam analysis');
    } finally {
      setLoading(false);
    }
  };

  React.useEffect(() => {
    if (open && !result) analyse();
  }, [open]);

  const toggleLayer = (id: string) => {
    setExpandedLayers(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const passed = result?.layers.filter(l => l.status === 'pass').length || 0;
  const failed = result?.layers.filter(l => l.status === 'fail').length || 0;
  const total = result?.layers.length || 0;

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      PaperProps={{ sx: { width: { xs: '100vw', sm: 420 }, bgcolor: '#f8fafc' } }}
    >
      {/* Header */}
      <Box sx={{ bgcolor: '#1e293b', color: '#fff', px: 2.5, py: 2, display: 'flex', alignItems: 'center', gap: 1.5 }}>
        <ShieldIcon sx={{ color: '#60a5fa' }} />
        <Box sx={{ flex: 1 }}>
          <Typography variant="subtitle1" fontWeight={700} sx={{ lineHeight: 1.2 }}>Spam Analysis</Typography>
          <Typography variant="caption" sx={{ color: '#94a3b8' }}>9-layer security check via Rspamd</Typography>
        </Box>
        <IconButton onClick={onClose} sx={{ color: '#94a3b8', p: 0.5 }}><CloseIcon /></IconButton>
      </Box>

      <Box sx={{ flex: 1, overflowY: 'auto', p: 2 }}>
        {loading && (
          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', py: 6, gap: 2 }}>
            <CircularProgress size={40} />
            <Typography variant="body2" color="text.secondary">Checking Rspamd history…</Typography>
          </Box>
        )}

        {error && !loading && (
          <Box sx={{ p: 2, bgcolor: '#fee2e2', borderRadius: 2, border: '1px solid #fca5a5' }}>
            <Typography variant="body2" color="error">{error}</Typography>
          </Box>
        )}

        {result && !loading && (
          <>
            {/* Score Card */}
            <Box sx={{ bgcolor: '#fff', borderRadius: 2, p: 2, mb: 2, border: '1px solid #e2e8f0', boxShadow: '0 1px 3px rgba(0,0,0,0.05)' }}>
              {result.found ? (
                <>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1.5 }}>
                    <Box>
                      <Typography variant="h4" fontWeight={800} sx={{ color: getScoreColor(result.score || 0), lineHeight: 1 }}>
                        {(result.score || 0).toFixed(1)}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">Spam Score</Typography>
                    </Box>
                    <Chip
                      label={(result.action || 'no action').toUpperCase()}
                      size="small"
                      sx={{
                        bgcolor: getActionColor(result.action || '') + '20',
                        color: getActionColor(result.action || ''),
                        fontWeight: 700, fontSize: '0.7rem',
                        border: `1px solid ${getActionColor(result.action || '')}40`,
                      }}
                    />
                  </Box>
                  <LinearProgress
                    variant="determinate"
                    value={Math.min(100, ((result.score || 0) / 20) * 100)}
                    sx={{
                      height: 6, borderRadius: 3, bgcolor: '#e2e8f0', mb: 1.5,
                      '& .MuiLinearProgress-bar': { bgcolor: getScoreColor(result.score || 0), borderRadius: 3 },
                    }}
                  />
                  <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                    <Box sx={{ display: 'flex', gap: 0.5, alignItems: 'center' }}>
                      <PassIcon sx={{ fontSize: 14, color: '#16a34a' }} />
                      <Typography variant="caption" color="text.secondary">{passed} passed</Typography>
                    </Box>
                    <Box sx={{ display: 'flex', gap: 0.5, alignItems: 'center' }}>
                      <FailIcon sx={{ fontSize: 14, color: '#dc2626' }} />
                      <Typography variant="caption" color="text.secondary">{failed} failed</Typography>
                    </Box>
                    {result.scanTime && (
                      <Typography variant="caption" color="text.secondary">Scanned in {result.scanTime}</Typography>
                    )}
                  </Box>
                  {result.scannedAt && (
                    <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
                      {new Intl.DateTimeFormat('en-IN', { timeZone: 'Asia/Kolkata', day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit', hour12: true }).format(new Date(result.scannedAt))} IST
                    </Typography>
                  )}
                </>
              ) : (
                <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'flex-start' }}>
                  <UnknownIcon sx={{ color: '#94a3b8', mt: 0.3 }} />
                  <Box>
                    <Typography variant="body2" fontWeight={600}>Not in scan history</Typography>
                    <Typography variant="caption" color="text.secondary">{result.summary}</Typography>
                  </Box>
                </Box>
              )}
            </Box>

            {/* Layer checks */}
            <Typography variant="overline" sx={{ px: 0.5, color: '#64748b', fontWeight: 700, fontSize: '0.65rem', letterSpacing: 1 }}>
              SECURITY LAYERS
            </Typography>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75, mt: 0.5, mb: 2 }}>
              {result.layers.map((layer) => {
                const cfg = STATUS_CONFIG[layer.status] || STATUS_CONFIG.unknown;
                const isExpanded = expandedLayers.has(layer.id);
                return (
                  <Box key={layer.id} sx={{ bgcolor: '#fff', borderRadius: 1.5, border: '1px solid #e2e8f0', overflow: 'hidden' }}>
                    <Box
                      sx={{ display: 'flex', alignItems: 'center', gap: 1, px: 1.5, py: 1, cursor: layer.triggered.length > 0 ? 'pointer' : 'default', '&:hover': layer.triggered.length > 0 ? { bgcolor: '#f8fafc' } : {} }}
                      onClick={() => layer.triggered.length > 0 && toggleLayer(layer.id)}
                    >
                      {cfg.icon}
                      <Box sx={{ flex: 1, minWidth: 0 }}>
                        <Typography variant="body2" fontWeight={600} sx={{ fontSize: '0.8rem' }}>{layer.name}</Typography>
                        <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.7rem' }}>{layer.description}</Typography>
                      </Box>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        {layer.score !== 0 && (
                          <Typography variant="caption" sx={{ color: layer.score > 0 ? '#dc2626' : '#16a34a', fontWeight: 600, fontSize: '0.7rem' }}>
                            {layer.score > 0 ? '+' : ''}{layer.score.toFixed(1)}
                          </Typography>
                        )}
                        <Chip label={cfg.label} size="small" sx={{ bgcolor: cfg.bg, color: cfg.color, fontWeight: 600, fontSize: '0.65rem', height: 18, '& .MuiChip-label': { px: 0.75 } }} />
                        {layer.triggered.length > 0 && (isExpanded ? <ExpandLessIcon sx={{ fontSize: 14, color: '#94a3b8' }} /> : <ExpandMoreIcon sx={{ fontSize: 14, color: '#94a3b8' }} />)}
                      </Box>
                    </Box>
                    <Collapse in={isExpanded}>
                      <Box sx={{ borderTop: '1px solid #f1f5f9', px: 1.5, py: 1, bgcolor: '#fafbfc' }}>
                        {layer.triggered.map((sym, i) => (
                          <Box key={i} sx={{ display: 'flex', justifyContent: 'space-between', gap: 1, py: 0.25 }}>
                            <Typography variant="caption" sx={{ fontFamily: 'monospace', color: '#374151', fontSize: '0.7rem' }}>{sym.name}</Typography>
                            <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                              {sym.description && <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.7rem' }}>{sym.description}</Typography>}
                              <Typography variant="caption" sx={{ color: sym.score > 0 ? '#dc2626' : '#16a34a', fontWeight: 600, fontSize: '0.7rem', minWidth: 30, textAlign: 'right' }}>
                                {sym.score > 0 ? '+' : ''}{sym.score.toFixed(2)}
                              </Typography>
                            </Box>
                          </Box>
                        ))}
                      </Box>
                    </Collapse>
                  </Box>
                );
              })}
            </Box>

            {/* All symbols toggle */}
            {result.found && result.allSymbols && result.allSymbols.length > 0 && (
              <>
                <Box
                  sx={{ display: 'flex', alignItems: 'center', gap: 0.5, cursor: 'pointer', px: 0.5, mb: 1 }}
                  onClick={() => setShowAllSymbols(!showAllSymbols)}
                >
                  <SecurityIcon sx={{ fontSize: 14, color: '#64748b' }} />
                  <Typography variant="overline" sx={{ color: '#64748b', fontWeight: 700, fontSize: '0.65rem', letterSpacing: 1, flex: 1 }}>
                    RAW SYMBOLS ({result.allSymbols.length})
                  </Typography>
                  {showAllSymbols ? <ExpandLessIcon sx={{ fontSize: 14 }} /> : <ExpandMoreIcon sx={{ fontSize: 14 }} />}
                </Box>
                <Collapse in={showAllSymbols}>
                  <Box sx={{ bgcolor: '#1e293b', borderRadius: 1.5, p: 1.5, maxHeight: 240, overflowY: 'auto' }}>
                    {result.allSymbols.map((sym, i) => (
                      <Box key={i} sx={{ display: 'flex', justifyContent: 'space-between', gap: 1, py: 0.2 }}>
                        <Typography sx={{ fontFamily: 'monospace', fontSize: '0.7rem', color: '#93c5fd' }}>{sym.name}</Typography>
                        <Typography sx={{ fontFamily: 'monospace', fontSize: '0.7rem', color: sym.score > 0 ? '#fca5a5' : '#86efac', fontWeight: 600, minWidth: 35, textAlign: 'right' }}>
                          {sym.score > 0 ? '+' : ''}{sym.score.toFixed(2)}
                        </Typography>
                      </Box>
                    ))}
                  </Box>
                </Collapse>
              </>
            )}
          </>
        )}
      </Box>

      {/* Footer */}
      <Box sx={{ px: 2, py: 1.5, borderTop: '1px solid #e2e8f0', bgcolor: '#fff', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Typography variant="caption" color="text.secondary">Powered by Rspamd</Typography>
        <Box
          onClick={() => { setResult(null); analyse(); }}
          sx={{ fontSize: '0.75rem', color: '#6366f1', cursor: 'pointer', fontWeight: 600, '&:hover': { textDecoration: 'underline' } }}
        >
          Re-check
        </Box>
      </Box>
    </Drawer>
  );
};

export default SpamAnalysisDrawer;
